import { useEffect, useState, useCallback } from 'react';
import { logger } from '../../utils';
import API from '../../modules/myAI/settings/api';
import {
  useSelectedBot,
  useSelectedClient,
  useAuthentication
} from '../../shell';

interface Agent {
  _id: string;
  type: 'bot' | 'team' | 'app';
  bot?: string;
  team?: string;
  app?: string;
}

interface Channel {
  _id?: string;
  type: 'messenger' | 'app' | 'facebook' | 'email' | 'teams' | 'whatsapp';
  enabled: boolean;
  config?: {
    appName?: string;
    appIconURL?: string;
    launcherIconURL?: string;
    aiEmailAddress?: string;
    appId?: string;
    appPassword?: string;
    accessToken?: string;
    verifyToken?: string;
    hasAccessToken?: boolean;
    appSecret?: string;
    hasAppSecret?: boolean;
    theme?: Record<string, unknown>;
  };
  allowedURLs?: string[];
  webhookId?: string;
  accessToken?: string;
}

interface PipelineState {
  pipeline: Partial<{
    _id: string;
    channels: Channel[];
    otherAgents: Record<string, Agent>;
  }>;
  loading: boolean;
}

export function usePipeline(): PipelineState & {
  update: (
    pipeline: string,
    botName: string,
    botAvatar: string
  ) => Promise<void>;
  addChannel: (channel: Channel) => Promise<boolean>;
  updateChannel: (channel: Channel) => Promise<boolean>;
  deleteChannel: (channelId: string) => Promise<boolean>;
  getChannelByType: (type: Channel['type']) => Channel | undefined;
} {
  const { token } = useAuthentication();
  const { selectedClient } = useSelectedClient();
  const { selectedBot } = useSelectedBot();

  const [state, setState] = useState<PipelineState>({
    loading: true,
    pipeline: { channels: [] }
  });

  useEffect(() => {
    async function run() {
      if (!selectedBot.pipelineId) {
        setState({
          loading: false,
          pipeline: {}
        });
        return;
      }

      const pipeline = (await API.getPipeline(
        process.env.AUTH_URL,
        selectedClient,
        token,
        selectedBot.pipelineId
      )) as PipelineState['pipeline'];

      setState({
        loading: false,
        pipeline
      });
    }

    run().catch(err => {
      logger(err);
    });
  }, [selectedBot.pipelineId, token, selectedClient]);

  const update = useCallback(
    async (newPipeline, botName, botAvatar) => {
      const { pipeline } = (await API.postUpdateTheme(
        process.env.AUTH_URL,
        selectedClient,
        selectedBot._id,
        token,
        botName,
        botAvatar,
        newPipeline,
        state.pipeline?._id
      )) as { pipeline: PipelineState['pipeline'] };
      setState({ pipeline, loading: false });
    },
    [selectedBot, selectedClient, token, state.pipeline]
  );

  const addChannel = useCallback(
    async (channel: Channel) => {
      if (!state.pipeline?._id) {
        return false;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const result = (await API.addChannel(
        process.env.AUTH_URL,
        selectedClient,
        token,
        state.pipeline._id,
        channel
      )) as unknown as PipelineState['pipeline'] | null;

      if (result) {
        setState(prev => ({ ...prev, pipeline: result }));
        return true;
      }
      return false;
    },
    [selectedClient, token, state.pipeline?._id]
  );

  const updateChannel = useCallback(
    async (channel: Channel) => {
      if (!state.pipeline?._id) {
        return false;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const result = (await API.updateChannel(
        process.env.AUTH_URL,
        selectedClient,
        token,
        state.pipeline._id,
        channel
      )) as unknown as PipelineState['pipeline'] | null;

      if (result) {
        setState(prev => ({ ...prev, pipeline: result }));
        return true;
      }
      return false;
    },
    [selectedClient, token, state.pipeline?._id]
  );

  const deleteChannel = useCallback(
    async (channelId: string) => {
      if (!state.pipeline?._id) {
        return false;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const result = (await API.deleteChannel(
        process.env.AUTH_URL,
        selectedClient,
        token,
        state.pipeline._id,
        channelId
      )) as unknown as PipelineState['pipeline'] | null;

      if (result) {
        setState(prev => ({ ...prev, pipeline: result }));
        return true;
      }
      return false;
    },
    [selectedClient, token, state.pipeline?._id]
  );

  const getChannelByType = useCallback(
    (type: Channel['type']) => {
      return state.pipeline?.channels?.find(channel => channel.type === type);
    },
    [state.pipeline?.channels]
  );

  return {
    ...state,
    update,
    addChannel,
    updateChannel,
    deleteChannel,
    getChannelByType
  };
}
